// @flow
import * as React from 'react';
import Cookies from 'js-cookie';
import type { Props } from './types';

const MAX_PROGRESS_ITEMS = 500;
const PROGRESS_RESET_THRESH = 98;

type State = {
  modalVis: boolean;
  url: string;
  store: string;
  storeImg: string;
}

export default class Player extends React.Component<Props, State> {
  elementId: string;
  node: HTMLElement;
  setupParams: Object;
  notFoundAudio: Audio;
  modalMsg: string;
  nodalLink: string;
  modalLinkAndroid: string;
  modalLinkIOS: string;
  totalDuration: number | null;
  IOSImage: string;
  AndroidImage: string;
  npoPlayer: any;

  constructor(props: Props): void {
    super(props);
    this.state = {
      modalVis: false,
      url: '',
      store: '',
      storeImg: '',
    };
    this.elementId = `player-id-${Math.floor(Math.random() * 1000 * 1000)}`;
    this.modalLinkAndroid = 'https://play.google.com/store/apps/details?id=nl.omroep.zappelin.android';
    this.modalLinkIOS = 'https://itunes.apple.com/nl/app/npo-zappelin/id428263410?mt=8';
    this.modalMsg = 'Jammer,  je kunt dit filmpje niet kijken. Kijk het filmpje in de Zappelin app.';
    this.IOSImage = '/assets/app-stores/app_store.png';
    this.AndroidImage = '/assets/app-stores/play_store.png';
    this.notFoundAudio = new Audio();
    if (this.notFoundAudio instanceof HTMLAudioElement) {
      this.notFoundAudio.src = props.notFoundAudio;
    }
    this.totalDuration = null;
    this.setupParams = {
      autoplay: props.autoplay,
      placeholder: props.placeholder,
      noAds: props.noAds,
      elementId: this.elementId,
      endscreen: props.endscreen,
      recommendations: props.recommendations,
      moreButton: props.moreButton,
      nextEpisode: props.nextEpisode,
      hasSettings: props.hasSettings,
      stylesheet: props.stylesheet,
      trackProgress: props.trackProgress,
    };
  }

  getProgress = (): number => {
    const allProgress = localStorage.getItem('zappelin_progress');
    if (allProgress) {
      const currentProgress = JSON.parse(allProgress).find(item => item.mid === this.props.mid);
      if (currentProgress) {
        return currentProgress.ratio >= PROGRESS_RESET_THRESH ? 0 : currentProgress.progress;
      }
    }
    return 0;
  }

  percToSec = (perc: number) : number => {
    if (this.totalDuration) {
      return (perc / 100) * this.totalDuration;
    }
    return 0.0;
  }

  secToPerc = (duration: number): number => {
    if (this.totalDuration) {
      return (100 / this.totalDuration) * duration;
    }
    return 0.0;
  }

  saveProgress = (progress: number): void => {
    if (!progress) return;
    const allProgress = localStorage.getItem('zappelin_progress');
    // Get progress from localStorage entry or create array
    const progressDb = allProgress ? JSON.parse(allProgress) : [];
    const progressObject = {
      mid: this.props.mid,
      progress: progress.toFixed(3),
      ratio: this.secToPerc(progress).toFixed(3),
    };
    const foundObject = progressDb.find(item => item.mid === this.props.mid);
    if (foundObject) { // entry for this mid is found
      // If the progress is within a rounded margin of the found progress, do nothing
      if (Math.round(parseFloat(foundObject.progress)) === Math.round(progress)) return;
      const index = progressDb.findIndex(item => item.mid === this.props.mid);
      // overwrite entry with new duration
      progressDb[index] = progressObject;
    } else {
      // no mid entry is found
      if (progressDb.length > MAX_PROGRESS_ITEMS - 1) {
        // remove the extra entries in the list
        progressDb.splice(0, progressDb.length - MAX_PROGRESS_ITEMS);
      }
      // Add a new duration entry at the end of the list
      progressDb.push(progressObject);
    }
    try {
      // save the list
      localStorage.setItem('zappelin_progress', JSON.stringify(progressDb));
    } catch (e) {
      throw e;
    }
  }

  handleTimeUpdate = (): void => {
    if (!this.npoPlayer) return;
    const time = this.npoPlayer.getCurrentTime();
    if (time > 0) {
      this.saveProgress(time);
    }
  }

  handleDurationChange = (): void => {
    if (!this.npoPlayer) return;
    const duration = this.npoPlayer.getDuration();
    if (duration > 0 && Number.isFinite(duration)) {
      this.totalDuration = duration;
    }
  }

  handleEnded = (): void => {
    if (this.props.onComplete) {
      this.props.onComplete();
    }
  }

  handleDrm = (): void => {
    if (window.navigator.userAgent.indexOf('iPhone') > -1 || window.navigator.userAgent.indexOf('iPad') > -1) {
      this.setState({ modalVis: true, url: this.modalLinkIOS, store: 'App Store', storeImg: this.IOSImage });
    } else if (window.navigator.userAgent.indexOf('SamsungBrowser') > -1 || window.navigator.userAgent.indexOf('Chrome') > -1) {
      this.setState({ modalVis: true, url: this.modalLinkAndroid, store: 'Play Store', storeImg: this.AndroidImage });
    }
  }

  handleError = (): void => {
    if (window.navigator.userAgent.indexOf('iPhone') > -1 || window.navigator.userAgent.indexOf('iPad') > -1) {
      this.setState({ modalVis: true, url: this.modalLinkIOS, store: 'App Store', storeImg: this.IOSImage });
    } else if (/android/i.test(window.navigator.userAgent)) {
      this.setState({ modalVis: true, url: this.modalLinkAndroid, store: 'Play Store', storeImg: this.AndroidImage });
    }
  }

  recommendation = (actualEvent: any): void => {
    window.location.href = `/mid/${actualEvent.video.id}`;
  }

  setSiteId = (): void => {
    const userId = Cookies.getJSON('atuserid').val;
    const iframe = ((document.getElementById(`iframe-${this.props.mid}`) : any) : HTMLIFrameElement);
    iframe.contentWindow.postMessage(JSON.stringify({ event: 'NPO_SET_SMARTTAG_USER_ID', userId }), '*');
  }

  async componentDidMount(): void {
    const response = await fetch('/api/app/v5/player_tokens', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ payload: this.props.playerTokenPayload }),
    });

    if (!response.ok) {
      // eslint-disable-next-line
      console.error('Failed to fetch JWT');
      return;
    }

    const data = await response.json();
    const jwt = data.token;

    const container = document.getElementById(this.elementId);

    const { NpoVideoPlayer } = window.NpoPlayer;
    const { NpoVideoPlayerUIFactory } = window.NpoVideoPlayerUIFactory;

    const player = new NpoVideoPlayer({
      mediaType: 'video',
      uiFactory: new NpoVideoPlayerUIFactory(),
      autoplay: this.setupParams.autoplay,
      options: {
        autoplay: this.setupParams.autoplay,
      },
      npoTag: {
        // The player builds the NPO Tag instance (and page tracker) from
        // `initprops`. The brand fields must be nested here, not directly under
        // npoTag; this is the same object v1 passed as its 3rd constructor arg.
        // Without it: "No npoTagInstance available".
        initprops: {
          brand: this.props.npotagBrand,
          brand_id: this.props.npotagBrandId,
          platform: this.props.npotagPlatform,
          platform_version: this.props.npotagPlatformVersion,
        },
      },
    }, container);
    this.npoPlayer = player;

    player.on('timeupdate', this.handleTimeUpdate);
    player.on('durationchange', this.handleDurationChange);
    player.on('ended', this.handleEnded);
    player.on('error', this.handleError);

    // #41: deliberately do NOT pass metadata.title. Supplied metadata overrules
    // the title the player fetches from the NPO API, and the view never gives us
    // a real series title - so the old `title || mid` fallback forced the PRID
    // into the title bar. Letting the API metadata through shows the real title.
    const streamOptions = { npotag: {} };

    // #45: only resume when we have a stored position. Sending startOffset:0 on
    // a live stream anchors playback to the start of the DVR window (grey live
    // dot) instead of the live edge (red dot); omitting it starts at the edge.
    const resumeAt = this.getProgress();
    if (resumeAt > 0) streamOptions.startOffset = resumeAt;

    // #48: native v2 binge, replacing the v1 EndScreen countdown. The SDK
    // registers a time-bound action over [duration - max(offset, countdown),
    // duration], shows its own overlay there and calls proceedCallback when the
    // countdown runs out - so offset < the countdown is silently ignored, keep
    // them equal. No-op on live streams and when the duration is unknown at
    // load. Only wired when the view supplied a next episode.
    if (this.props.nextUrl) {
      const { nextUrl } = this.props;
      streamOptions.playNext = {
        showPlayNext: true,
        offset: 10, // ponytail: last 10s, so we advance at the end like v1 did.
        duration: 10, // Raise both together to start the next episode over the credits.
        proceedCallback: () => { window.location.href = nextUrl; },
      };
    }

    try {
      await player.load(jwt, streamOptions);
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error('NPO Player v2 load failed', e);
      this.handleError();
    }
  }

  onVideoNotFound = (): Promise<*> => (
    new Promise((resolve, reject) => {
      const waitForElement = () => {
        const player = document.querySelector(`#${this.elementId}`);
        const msg = document.querySelector('.npo-player-overlay-message');
        if (!!msg && !player) {
          resolve(msg);
        } else if (!msg && !!player) {
          reject(new Error("Video can't be shown."));
        } else {
          window.requestAnimationFrame(waitForElement);
        }
      };
      waitForElement();
    })
  )

  componentWillUnmount(): void {
    if (this.npoPlayer && typeof this.npoPlayer.destroy === 'function') {
      this.npoPlayer.destroy();
    }
  }

  render(): any {
    return (
      <React.Fragment>
        {this.state.modalVis &&
          (<div className="player__store-modal" >
            <div className="player__inner-modal">
              <p>{ this.modalMsg }</p>
              <br />
              <div className="player__modal-url">
                <a href={this.state.url}>
                  <img src={this.state.storeImg} className="player__store-img" alt="storeImg" />
                </a>
              </div>
            </div>
          </div>)
        }
        <div className="player__body" id={this.elementId} />
      </React.Fragment>
    );
  }
}
